import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { socketService } from "../../../services/socket";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isToday,
} from "date-fns";
import { es } from "date-fns/locale";

interface Task {
  id: string;
  date: string;
  phaseId: string;
  title: string;
  description: string;
  completed: boolean;
}

interface Phase {
  id: string;
  name: string;
  color: string;
  startDate: string;
  endDate: string;
}

interface RoadmapData {
  phases: Phase[];
  tasks: Task[];
}

export const RoadmapView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<RoadmapData>({ phases: [], tasks: [] });
  const [currentDate, setCurrentDate] = useState(new Date(2026, 0, 1)); // Start at Jan 2026

  useEffect(() => {
    const handleData = (newData: RoadmapData) => {
      setData(newData);
    };

    socketService.on("roadmap:data", handleData);
    socketService.emit("roadmap:get");

    return () => {
      socketService.off("roadmap:data", handleData);
    };
  }, []);

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentDate),
    end: endOfMonth(currentDate),
  });

  const toggleTask = (taskId: string, currentStatus: boolean) => {
    // Optimistic update
    setData((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t) => (t.id === taskId ? { ...t, completed: !currentStatus } : t)),
    }));
    socketService.emit("roadmap:toggle-task", { taskId, completed: !currentStatus });
  };

  const getPhaseColor = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const phase = data.phases.find((p) => dateStr >= p.startDate && dateStr <= p.endDate);
    return phase ? phase.color : "transparent";
  };

  const completionPercentage = React.useMemo(() => {
    if (data.tasks.length === 0) return 0;
    const completed = data.tasks.filter((t) => t.completed).length;
    return Math.round((completed / data.tasks.length) * 100);
  }, [data.tasks]);

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        background: "var(--color-bg-mesh)",
        color: "var(--color-text-primary)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          padding: "20px 40px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid var(--color-panel-border)",
          background: "var(--color-panel-bg)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <button
            onClick={() => navigate("/projects")}
            style={{
              background: "transparent",
              border: "1px solid var(--color-panel-border)",
              color: "var(--color-text-secondary)",
              padding: "8px 16px",
              borderRadius: "var(--border-radius-sm)",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {t("roadmap.back")}
          </button>
          <h1
            style={{
              margin: 0,
              fontSize: "1.8rem",
              letterSpacing: "1px",
              color: "var(--color-text-primary)",
            }}
          >
            {t("roadmap.title")}
          </h1>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "30px" }}>
          {/* PROGRESS BAR */}
          <div style={{ display: "flex", flexDirection: "column", gap: "5px", width: "250px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.8rem",
                color: "var(--color-text-muted)",
              }}
            >
              <span>{t("roadmap.totalProgress")}</span>
              <span style={{ color: "var(--color-text-primary)", fontWeight: "bold" }}>
                {completionPercentage}%
              </span>
            </div>
            <div
              style={{
                height: "8px",
                background: "var(--color-panel-border)",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${completionPercentage}%`,
                  background: "linear-gradient(90deg, #f72d60, #7a81ff)",
                  transition: "width 0.5s ease",
                }}
              />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              onClick={() => setCurrentDate(subMonths(currentDate, 1))}
              style={{
                background: "var(--color-panel-border)",
                border: "none",
                color: "#fff",
                padding: "10px",
                borderRadius: "50%",
                cursor: "pointer",
              }}
            >
              ◀
            </button>
            <h2 style={{ minWidth: "200px", textAlign: "center", margin: 0, fontSize: "1.4rem" }}>
              {format(currentDate, "MMMM yyyy", { locale: es }).toUpperCase()}
            </h2>
            <button
              onClick={() => setCurrentDate(addMonths(currentDate, 1))}
              style={{
                background: "var(--color-panel-border)",
                border: "none",
                color: "#fff",
                padding: "10px",
                borderRadius: "50%",
                cursor: "pointer",
              }}
            >
              ▶
            </button>
          </div>
        </div>
      </div>

      {/* PHASES LEGEND */}
      <div
        style={{
          padding: "10px 40px",
          display: "flex",
          gap: "20px",
          background: "var(--color-panel-bg)",
          borderBottom: "1px solid var(--color-panel-border)",
        }}
      >
        {data.phases.map((p) => (
          <div
            key={p.id}
            style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.9rem" }}
          >
            <div
              style={{ width: "12px", height: "12px", borderRadius: "50%", background: p.color }}
            />
            <span style={{ color: "var(--color-text-secondary)", fontWeight: "bold" }}>
              {p.name}
            </span>
          </div>
        ))}
      </div>

      {/* CALENDAR GRID */}
      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gridAutoRows: "minmax(120px, 1fr)",
          padding: "20px",
          gap: "10px",
          overflowY: "auto",
        }}
      >
        {[
          t("roadmap.weekdays.sun"),
          t("roadmap.weekdays.mon"),
          t("roadmap.weekdays.tue"),
          t("roadmap.weekdays.wed"),
          t("roadmap.weekdays.thu"),
          t("roadmap.weekdays.fri"),
          t("roadmap.weekdays.sat"),
        ].map((day) => (
          <div
            key={day}
            style={{
              textAlign: "center",
              color: "var(--color-text-muted)",
              fontWeight: "bold",
              paddingBottom: "10px",
              borderBottom: "1px solid var(--color-panel-border)",
            }}
          >
            {day}
          </div>
        ))}

        {/* Empty cells for offset */}
        {Array.from({ length: startOfMonth(currentDate).getDay() }).map((_, i) => (
          <div key={`empty-${i}`} style={{ background: "transparent" }} />
        ))}

        {daysInMonth.map((day: Date) => {
          const dateStr = format(day, "yyyy-MM-dd");
          const dayTasks = data.tasks.filter((t) => t.date === dateStr);
          const isTodayDate = isToday(day);
          const phaseColor = getPhaseColor(day);
          const isPast = day < new Date() && !isTodayDate;

          return (
            <div
              key={dateStr}
              style={{
                background: isTodayDate ? "var(--color-panel-bg)" : "var(--color-input-bg)",
                border: `1px solid ${isTodayDate ? "var(--color-accent-danger)" : "var(--color-panel-border)"}`,
                borderRadius: "8px",
                padding: "10px",
                opacity: isPast ? 0.8 : 1,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "0.9rem",
                  color: isTodayDate ? "var(--color-accent-danger)" : "var(--color-text-secondary)",
                  fontWeight: "bold",
                  marginBottom: "5px",
                }}
              >
                <span>{format(day, "d")}</span>
                {phaseColor !== "transparent" && (
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: phaseColor,
                    }}
                  />
                )}
              </div>

              {dayTasks.map((task) => (
                <div
                  key={task.id}
                  onClick={() => toggleTask(task.id, task.completed)}
                  style={{
                    background: task.completed
                      ? "rgba(34, 197, 94, 0.15)"
                      : "rgba(255, 255, 255, 0.05)",
                    borderLeft: `3px solid ${task.completed ? "var(--color-accent-success)" : getPhaseColor(day) || "var(--color-text-muted)"}`,
                    padding: "8px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: "bold",
                      color: task.completed
                        ? "var(--color-accent-success)"
                        : "var(--color-text-primary)",
                      textDecoration: task.completed ? "line-through" : "none",
                      marginBottom: "4px",
                    }}
                  >
                    {task.title}
                  </div>
                  <div
                    style={{
                      fontSize: "0.7rem",
                      color: "var(--color-text-muted)",
                      lineHeight: "1.2",
                    }}
                  >
                    {task.description}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      marginTop: "4px",
                      fontSize: "0.7rem",
                    }}
                  >
                    {task.completed ? t("roadmap.done") : t("roadmap.pending")}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};
